/**
 * PrepPod.jsx — /prep-pod public landing page.
 *
 * A single-file, self-contained conversion machine. Section order matches
 * the design guidelines blueprint: Hero → PRSVR Pillars → SAA Origin →
 * Daily Rotation Timeline → Location → Booking Widget → Application Form →
 * Pricing → Footer.
 *
 * Palette (locked, from /app/design_guidelines.json):
 *   ink               #0A0A0F
 *   card              #14141B
 *   kinetic_volt      #FFC72C   ← primary Nike energy accent
 *   athletic_red      #E63946
 *   cocomelon_mint    #A8E6CF
 *   cocomelon_peach   #FFD3B6
 *   cocomelon_sky     #A8DADC
 *
 * Fonts: Montserrat + Barlow Condensed (headline) + Plus Jakarta Sans
 * (accent). Loaded via Google Fonts inside this page only so we don't
 * pollute other routes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Calendar, Check, ChevronRight, Clock, FileText, Heart, Loader2, Mail, MapPin,
  Phone, Sparkles, User, Users, X, Zap, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// ─── Palette (kept in one place so we can tweak) ───────────────────────────
const C = {
  ink: '#0A0A0F',
  card: '#14141B',
  volt: '#FFC72C',
  red: '#E63946',
  mint: '#A8E6CF',
  peach: '#FFD3B6',
  sky: '#A8DADC',
  chalk: '#FAF8F5',
  subtle: '#27272A',
  dim: '#A1A1AA',
};

const FONT_LINK_ID = 'prep-pod-fonts';

// ─── Static content ────────────────────────────────────────────────────────
const PRSVR = [
  { letter: 'P', word: 'Perseverance', color: C.volt, blurb: 'Show up. Every rep, every read, every rotation.' },
  { letter: 'R', word: 'Resilience', color: C.mint, blurb: 'Fall down. Get up. Try the harder problem.' },
  { letter: 'S', word: 'Sacrifice', color: C.peach, blurb: 'Trade the easy path for the great one.' },
  { letter: 'V', word: 'Values', color: C.sky, blurb: 'How we show up when nobody\'s watching.' },
  { letter: 'R', word: 'Respect', color: C.red, blurb: 'For coaches, for each other, for the work.' },
];

const SCHEDULE_BLOCKS = [
  { i: 1, name: 'Reading & Literacy', kicker: 'Power Block', detail: 'Phonics, vocabulary, active writing.', accent: C.volt, mins: 20 },
  { i: 2, name: 'Advanced Math', kicker: 'Core Rotation', detail: 'Problem-solving, number sense, foundational logic.', accent: C.mint, mins: 20 },
  { i: 3, name: 'Coordination & Movement', kicker: 'Athletic Reset', detail: 'Agility, balance training, athletic fundamentals.', accent: C.red, mins: 20 },
  { i: 4, name: 'Recharge & Snack', kicker: 'Reset', detail: 'Guided social time and hydration break.', accent: C.peach, mins: 15 },
  { i: 5, name: 'Extended Creative Lab', kicker: 'Studio', detail: 'Hands-on science experiments & fine arts projects.', accent: C.sky, mins: 30 },
  { i: 6, name: 'Synthesis & Recap', kicker: 'Wrap', detail: 'Interactive device learning, workbook application, wrap-up.', accent: C.volt, mins: 15 },
];

const GRADES = ['TK', 'K', '1st', '2nd'];
const COUNTING = [
  { v: 'up_to_10', label: 'Up to 10' },
  { v: 'up_to_20', label: 'Up to 20' },
  { v: 'up_to_50', label: 'Up to 50' },
  { v: '50_plus', label: '50+ / 100+' },
];
const LITERACY = [
  { v: 'letter_recognition', label: 'Letter Recognition' },
  { v: 'letter_sounds', label: 'Letter Sounds' },
  { v: 'blending', label: 'Blending Words' },
  { v: 'fluent', label: 'Fluent Reading' },
];
const FUNDING = [
  { v: 'taylion', label: 'Taylion Academy Charter', accent: C.mint },
  { v: 'epic_california', label: 'Epic California Academy Charter', accent: C.sky },
  { v: 'private_pay', label: 'Private Pay (Direct)', accent: C.volt },
];
const PRICING = [
  { id: 'drop_in',  title: 'Drop-In',      sub: 'Single Session',  per: '$50',  unit: '1 session',  badge: null },
  { id: 'weekly',   title: 'Weekly Pod',   sub: 'Two Sessions',    per: '$80',  unit: '2 sessions', badge: null },
  { id: 'monthly',  title: 'Monthly Pod',  sub: 'Eight Sessions',  per: '$300', unit: '8 sessions', badge: 'Best Value', highlight: true },
];

// ═══════════════════════════════════════════════════════════════════════════
// Root component
// ═══════════════════════════════════════════════════════════════════════════
export default function PrepPod() {
  // Inject Google Fonts once, scoped to this page.
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Montserrat:wght@400;600;700;900&family=Plus+Jakarta+Sans:wght@500;700&display=swap';
    document.head.appendChild(link);
    document.title = 'THE PREP POD · Advanced TK–2nd Common Core & Elective Pod';
  }, []);

  const [confirmation, setConfirmation] = useState(null); // {title, subtitle}

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: C.ink, color: '#fff', fontFamily: 'Montserrat, sans-serif' }}
      data-testid="prep-pod-landing"
    >
      <Hero />
      <PillarStrip />
      <SaaOrigin />
      <PricingSection onCharterRequested={setConfirmation} />
      <TipSection />
      <ScheduleTimeline />
      <LocationCard />
      <ApplicationForm onConfirmed={setConfirmation} />
      <Footer />

      <AnimatePresence>
        {confirmation && (
          <ConfirmationModal payload={confirmation} onClose={() => setConfirmation(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hero
// ═══════════════════════════════════════════════════════════════════════════
const Hero = () => (
  <section
    className="relative overflow-hidden pt-14 pb-16 md:pt-24 md:pb-28"
    style={{
      background: `radial-gradient(ellipse 80% 60% at 20% 10%, ${C.volt}18 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 90% 90%, ${C.mint}12 0%, transparent 60%), ${C.ink}`,
    }}
    data-testid="prep-pod-hero"
  >
    {/* Diagonal athletic sweep */}
    <div
      aria-hidden
      className="absolute inset-y-0 right-0 w-1/2 hidden md:block"
      style={{
        background: `linear-gradient(115deg, transparent 40%, ${C.volt}08 60%, transparent 90%)`,
      }}
    />

    <div className="relative container mx-auto max-w-6xl px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="inline-flex items-center gap-2 border-2 px-4 py-1.5 mb-8"
        style={{ borderColor: C.volt, color: C.volt }}
      >
        <Sparkles className="w-3 h-3" />
        <span className="text-[10px] font-black tracking-[0.28em] uppercase">
          A PRSVR Education × Scholar Athlete Academy Production
        </span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="uppercase tracking-tight leading-[0.9]"
        style={{
          fontFamily: 'Barlow Condensed, Montserrat, sans-serif',
          fontWeight: 900,
          fontSize: 'clamp(3rem, 9vw, 7rem)',
        }}
      >
        THE <span style={{ color: C.volt }}>PREP</span> POD
      </motion.h1>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="mt-4 md:mt-6 max-w-3xl"
      >
        <div
          className="text-lg md:text-2xl font-bold leading-snug"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Advanced TK–2nd Grade Common Core &amp; Elective Pod at Handlelife HQ.
        </div>
        <div className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: C.dim }}>
          Fun, hands-on learning + dynamic physical movement + trying new things.
          Small pods, accelerated academics, real athletes-in-training.
        </div>
      </motion.div>

      {/* Quick facts strip */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.35 }}
        className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.2em] font-bold"
        style={{ color: C.dim }}
      >
        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" style={{ color: C.volt }} /> Chatsworth, CA</span>
        <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" style={{ color: C.volt }} /> Mon &amp; Wed · 9:30–11:00 AM</span>
        <span className="flex items-center gap-1.5"><Users className="w-3 h-3" style={{ color: C.volt }} /> TK · K · 1st · 2nd</span>
      </motion.div>

      {/* Dual CTA */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45 }}
        className="mt-10 flex flex-col sm:flex-row gap-3"
      >
        <a
          href="#pricing"
          className="group inline-flex items-center justify-center gap-2 px-7 py-4 text-xs font-black uppercase tracking-[0.22em] transition-transform hover:-translate-y-0.5"
          style={{ background: C.volt, color: C.ink }}
          data-testid="hero-see-packages-button"
        >
          See Packages <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
        <a
          href="#apply"
          className="group inline-flex items-center justify-center gap-2 px-7 py-4 text-xs font-black uppercase tracking-[0.22em] border-2 transition-colors hover:bg-white/5"
          style={{ borderColor: '#fff', color: '#fff' }}
          data-testid="hero-apply-button"
        >
          Apply Now <ChevronRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════
// PRSVR pillar strip
// ═══════════════════════════════════════════════════════════════════════════
const PillarStrip = () => (
  <section
    className="py-14 md:py-24 border-y"
    style={{ borderColor: C.subtle, background: C.ink }}
    data-testid="prep-pod-pillars"
  >
    <div className="container mx-auto max-w-6xl px-6">
      <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
        The Framework
      </div>
      <div
        className="uppercase leading-[0.95] mb-10 md:mb-16"
        style={{
          fontFamily: 'Barlow Condensed, Montserrat, sans-serif',
          fontWeight: 900,
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
        }}
      >
        Five Words. Every <span style={{ color: C.volt }}>Rep.</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-5">
        {PRSVR.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group p-6 border-2 transition-all hover:-translate-y-1"
            style={{ background: C.card, borderColor: C.subtle }}
            data-testid={`prsVr-pillar-card-${p.word.toLowerCase()}`}
          >
            <div
              className="text-6xl md:text-7xl mb-3 leading-none"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                color: p.color,
              }}
            >
              {p.letter}
            </div>
            <div className="text-sm font-black uppercase tracking-widest mb-2">{p.word}</div>
            <div className="text-xs leading-relaxed" style={{ color: C.dim }}>{p.blurb}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════
// SAA Origin Story
// ═══════════════════════════════════════════════════════════════════════════
const SaaOrigin = () => (
  <section className="py-14 md:py-24" style={{ background: C.ink }} data-testid="prep-pod-saa-origin">
    <div className="container mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <div>
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
          What Is SAA?
        </div>
        <h2
          className="uppercase leading-[0.95] mb-6"
          style={{
            fontFamily: 'Barlow Condensed, Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.25rem, 6vw, 4rem)',
          }}
        >
          Scholar Athlete <span style={{ color: C.volt }}>Academy.</span>
        </h2>
        <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: C.dim }}>
          An elite training ecosystem that merges high-level athletic movement with rigorous,
          accelerated academic foundational blocks — building well-rounded scholar-athletes from early childhood.
        </p>
        <p className="text-sm md:text-base leading-relaxed" style={{ color: C.dim }}>
          The Prep Pod is where it all begins: your TK–2nd grader learns like a scholar and moves like an athlete —
          every session, twice a week.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { n: '90 MIN', l: 'Rotation Per Day' },
          { n: 'MON + WED', l: 'Twice Weekly' },
          { n: 'SMALL', l: 'Pod Ratios' },
        ].map((s, i) => (
          <div
            key={i}
            className="p-5 border-2 text-center"
            style={{ background: C.card, borderColor: C.subtle }}
          >
            <div
              className="mb-2"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: 900,
                color: C.volt,
              }}
            >
              {s.n}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: C.dim }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════
// Schedule Timeline
// ═══════════════════════════════════════════════════════════════════════════
const ScheduleTimeline = () => {
  const [active, setActive] = useState(0);
  return (
    <section
      className="py-14 md:py-24"
      style={{ background: `linear-gradient(180deg, ${C.ink} 0%, ${C.card} 100%)` }}
      data-testid="prep-pod-schedule"
    >
      <div className="container mx-auto max-w-6xl px-6">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
          The Daily Rotation
        </div>
        <h2
          className="uppercase leading-[0.95] mb-3"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}
        >
          Ninety Minutes. <span style={{ color: C.volt }}>Six Rotations.</span>
        </h2>
        <p className="mb-10 text-sm md:text-base max-w-2xl" style={{ color: C.dim }}>
          9:30 AM – 11:00 AM, Mondays &amp; Wednesdays. Every block builds the next.
        </p>

        {/* Blocks */}
        <div className="grid gap-3">
          {SCHEDULE_BLOCKS.map((b, i) => {
            const open = active === i;
            return (
              <button
                key={b.i}
                onClick={() => setActive(open ? -1 : i)}
                className="text-left border-2 transition-all overflow-hidden"
                style={{
                  background: open ? '#1B1B24' : C.card,
                  borderColor: open ? b.accent : C.subtle,
                }}
                data-testid={`schedule-block-${b.name.toLowerCase().replace(/\s+/g, '-').replace('&','and')}`}
              >
                <div className="flex items-center gap-4 md:gap-6 p-4 md:p-6">
                  <div
                    className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 flex items-center justify-center"
                    style={{
                      background: b.accent,
                      color: C.ink,
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontWeight: 900,
                      fontSize: '2rem',
                    }}
                  >
                    {b.i}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-black uppercase tracking-[0.24em] mb-1" style={{ color: b.accent }}>
                      {b.kicker}
                    </div>
                    <div className="text-base md:text-xl font-black uppercase tracking-tight">{b.name}</div>
                  </div>
                  <div className="hidden sm:block text-xs font-bold uppercase tracking-widest" style={{ color: C.dim }}>
                    {b.mins} min
                  </div>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: b.accent }} />
                </div>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="px-4 md:px-24 pb-4 md:pb-6"
                    >
                      <div className="text-sm md:text-base leading-relaxed" style={{ color: C.dim }}>{b.detail}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Location card
// ═══════════════════════════════════════════════════════════════════════════
const LocationCard = () => (
  <section className="py-14 md:py-24" style={{ background: C.ink }} data-testid="prep-pod-location">
    <div className="container mx-auto max-w-6xl px-6">
      <div
        className="p-6 md:p-12 border-2 grid md:grid-cols-2 gap-8 items-center"
        style={{ background: C.card, borderColor: C.subtle }}
      >
        <div>
          <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
            The Location
          </div>
          <h2
            className="uppercase leading-[0.95] mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.25rem)' }}
          >
            Handlelife <span style={{ color: C.volt }}>HQ.</span>
          </h2>
          <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: C.dim }}>
            Sessions run on-site at Handlelife HQ — an elite training facility in Chatsworth, CA, tucked away
            in a safe private corner away from busy street traffic. Drop-off happens right at the door.
          </p>
          <div className="flex items-center gap-2 text-sm md:text-base font-bold">
            <MapPin className="w-4 h-4" style={{ color: C.volt }} />
            <a
              href="https://maps.google.com/?q=20611+Prairie+St,+Chatsworth,+CA"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: C.chalk }}
              data-testid="location-map-link"
            >
              20611 Prairie St, Chatsworth, CA
            </a>
          </div>
        </div>
        <div
          className="aspect-video w-full flex items-center justify-center relative overflow-hidden"
          style={{
            background: `radial-gradient(ellipse 60% 80% at 50% 50%, ${C.volt}22, transparent 70%), ${C.ink}`,
            border: `1px solid ${C.subtle}`,
          }}
        >
          <div className="text-center px-4">
            <MapPin className="w-12 h-12 mx-auto mb-3" style={{ color: C.volt }} />
            <div className="text-xs font-black uppercase tracking-[0.28em]" style={{ color: C.dim }}>Safe · Tucked-Away · Private</div>
            <div className="text-2xl md:text-3xl font-black mt-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Off The Main Street.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);


// ═══════════════════════════════════════════════════════════════════════════
// Application Form (multi-step)
// ═══════════════════════════════════════════════════════════════════════════
const ApplicationForm = ({ onConfirmed }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    child_name: '', child_dob: '', child_age: '', target_grade: '',
    counting_level: '', literacy_level: '',
    funding: '', pricing_tier: '', pricing_cycle: 'monthly',
    notes: '', website: '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const canAdvance = useMemo(() => {
    if (step === 1) return f.parent_name && f.parent_email && f.parent_phone;
    if (step === 2) return f.child_name && f.target_grade;
    if (step === 3) return f.counting_level && f.literacy_level;
    if (step === 4) return f.funding;
    return true;
  }, [step, f]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/prep-pod/application', {
        ...f,
        child_age: f.child_age ? parseInt(f.child_age, 10) : null,
      });
      if (r.data.success) {
        onConfirmed({
          title: 'Application received',
          subtitle: 'Margaret will personally reach out within 24 hours.',
        });
        setF({
          parent_name: '', parent_email: '', parent_phone: '',
          child_name: '', child_dob: '', child_age: '', target_grade: '',
          counting_level: '', literacy_level: '',
          funding: '', pricing_tier: '', pricing_cycle: 'monthly',
          notes: '', website: '',
        });
        setStep(1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit — try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="py-14 md:py-24 scroll-mt-8" style={{ background: C.ink }} data-testid="application-multi-step-form">
      <div className="container mx-auto max-w-4xl px-6">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
          Ready To Roll?
        </div>
        <h2 className="uppercase leading-[0.95] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.25rem)' }}>
          Apply <span style={{ color: C.volt }}>Now.</span>
        </h2>
        <p className="mb-8 text-sm md:text-base" style={{ color: C.dim }}>Four quick steps. About 2 minutes.</p>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className="flex-1 h-1"
              style={{ background: step >= s ? C.volt : C.subtle }}
            />
          ))}
        </div>

        <div
          className="p-6 md:p-10 border-2"
          style={{ background: C.card, borderColor: C.subtle }}
        >
          <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-6" style={{ color: C.volt }}>
            Step {step} / 4 · {['Parent', 'Student', 'Assessment', 'Funding'][step - 1]}
          </div>

          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Your Full Name" value={f.parent_name} onChange={(v) => set('parent_name', v)} testId="app-parent-name" />
              <Input label="Email" type="email" value={f.parent_email} onChange={(v) => set('parent_email', v)} testId="app-parent-email" />
              <div className="sm:col-span-2"><Input label="Mobile / Text" type="tel" value={f.parent_phone} onChange={(v) => set('parent_phone', v)} testId="app-parent-phone" /></div>
            </div>
          )}
          {step === 2 && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Child's Full Name" value={f.child_name} onChange={(v) => set('child_name', v)} testId="app-child-name" />
              <Input label="Age" type="number" value={f.child_age} onChange={(v) => set('child_age', v)} testId="app-child-age" />
              <Input label="Date of Birth (optional)" type="date" value={f.child_dob} onChange={(v) => set('child_dob', v)} testId="app-child-dob" />
              <div>
                <Label>Target Grade</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {GRADES.map((g) => (
                    <button
                      key={g}
                      onClick={() => set('target_grade', g)}
                      className="py-2.5 border-2 text-xs font-black uppercase tracking-widest"
                      style={{
                        background: f.target_grade === g ? C.volt : 'transparent',
                        color: f.target_grade === g ? C.ink : '#fff',
                        borderColor: f.target_grade === g ? C.volt : C.subtle,
                      }}
                      data-testid={`app-grade-${g}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <Label>Counting Ability</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {COUNTING.map((c) => (
                    <button
                      key={c.v}
                      onClick={() => set('counting_level', c.v)}
                      className="p-3 border-2 text-xs font-black uppercase tracking-widest"
                      style={{
                        background: f.counting_level === c.v ? C.mint : 'transparent',
                        color: f.counting_level === c.v ? C.ink : '#fff',
                        borderColor: f.counting_level === c.v ? C.mint : C.subtle,
                      }}
                      data-testid={`app-counting-${c.v}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Literacy Level</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {LITERACY.map((c) => (
                    <button
                      key={c.v}
                      onClick={() => set('literacy_level', c.v)}
                      className="p-3 border-2 text-xs font-black uppercase tracking-widest"
                      style={{
                        background: f.literacy_level === c.v ? C.sky : 'transparent',
                        color: f.literacy_level === c.v ? C.ink : '#fff',
                        borderColor: f.literacy_level === c.v ? C.sky : C.subtle,
                      }}
                      data-testid={`app-literacy-${c.v}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <Label>Funding Source</Label>
                <div className="space-y-2 mt-2">
                  {FUNDING.map((c) => (
                    <button
                      key={c.v}
                      onClick={() => set('funding', c.v)}
                      className="w-full p-4 border-2 text-left flex items-center justify-between transition-colors"
                      style={{
                        background: f.funding === c.v ? '#1B1B24' : 'transparent',
                        borderColor: f.funding === c.v ? c.accent : C.subtle,
                      }}
                      data-testid={`app-funding-${c.v}`}
                    >
                      <span className="text-sm font-black uppercase tracking-widest">{c.label}</span>
                      {f.funding === c.v ? <Check className="w-5 h-5" style={{ color: c.accent }} /> : null}
                    </button>
                  ))}
                </div>
              </div>
              <Input label="Anything else you'd like Margaret to know? (optional)" value={f.notes} onChange={(v) => set('notes', v)} testId="app-notes" />
            </div>
          )}

          <input type="text" value={f.website} onChange={(e) => set('website', e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />

          <div className="flex justify-between items-center mt-8 gap-2">
            <button
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="text-xs font-black uppercase tracking-[0.22em] py-3 px-5 border-2 disabled:opacity-30"
              style={{ borderColor: C.subtle, color: '#fff' }}
              data-testid="app-back-button"
            >
              Back
            </button>
            {step < 4 ? (
              <button
                disabled={!canAdvance}
                onClick={() => setStep((s) => s + 1)}
                className="text-xs font-black uppercase tracking-[0.22em] py-3 px-6 disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: C.volt, color: C.ink }}
                data-testid="app-next-button"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                disabled={!canAdvance || submitting}
                onClick={submit}
                className="text-xs font-black uppercase tracking-[0.22em] py-3 px-6 disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: C.volt, color: C.ink }}
                data-testid="app-submit-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit Application
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Pricing + Charter Invoice Request
// ═══════════════════════════════════════════════════════════════════════════
const PricingSection = ({ onCharterRequested }) => {
  const [charterOpen, setCharterOpen] = useState(false);
  const [charterForm, setCharterForm] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    charter: 'taylion', child_name: '', target_grade: '', pricing_tier: '', notes: '', website: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Stripe checkout modal state — captures parent/child info, then redirects.
  const [checkoutTier, setCheckoutTier] = useState(null); // one of PRICING entries or null
  const [checkoutForm, setCheckoutForm] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    child_name: '', child_grade: '', website: '',
  });
  const [payLoading, setPayLoading] = useState(false);

  const setPay = (k, v) => setCheckoutForm((p) => ({ ...p, [k]: v }));

  const openCheckout = (tier) => {
    setCheckoutTier(tier);
    setCheckoutForm({
      parent_name: '', parent_email: '', parent_phone: '',
      child_name: '', child_grade: '', website: '',
    });
  };

  const startCheckout = async () => {
    if (!checkoutTier) return;
    if (!checkoutForm.parent_name || !checkoutForm.parent_email) {
      return toast.error('Parent name and email are required');
    }
    setPayLoading(true);
    try {
      const r = await api.post('/prep-pod/checkout', {
        tier: checkoutTier.id,
        parent_name: checkoutForm.parent_name,
        parent_email: checkoutForm.parent_email,
        parent_phone: checkoutForm.parent_phone,
        child_name: checkoutForm.child_name,
        child_grade: checkoutForm.child_grade,
        origin: window.location.origin,
        website: checkoutForm.website,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.error('Could not create the checkout — try again');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment session failed to open — try again');
    } finally {
      setPayLoading(false);
    }
  };

  const setC = (k, v) => setCharterForm((p) => ({ ...p, [k]: v }));

  const submitCharter = async () => {
    if (!charterForm.parent_name || !charterForm.parent_email) return toast.error('Name + email required');
    setSubmitting(true);
    try {
      const r = await api.post('/prep-pod/charter-request', charterForm);
      if (r.data.success) {
        onCharterRequested({
          title: 'Charter request submitted',
          subtitle: 'Margaret will initiate the invoice/vendor packet with your charter.',
        });
        setCharterOpen(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit — try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="pricing" className="py-14 md:py-24 scroll-mt-8" style={{ background: C.card }} data-testid="prep-pod-pricing">
      <div className="container mx-auto max-w-6xl px-6">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
          Simple, Session-Based
        </div>
        <h2 className="uppercase leading-[0.95] mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.25rem)' }}>
          Pick Your <span style={{ color: C.volt }}>Package.</span>
        </h2>
        <p className="text-sm md:text-base mb-10 max-w-2xl" style={{ color: C.dim }}>
          One-time payments — no auto-renew, no subscription trap. Pay per package, use the sessions on your schedule.
        </p>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {PRICING.map((p) => (
            <div
              key={p.id}
              className="p-6 md:p-8 border-2 flex flex-col relative"
              style={{
                background: p.highlight ? C.ink : 'transparent',
                borderColor: p.highlight ? C.volt : C.subtle,
              }}
            >
              {p.badge && (
                <div
                  className="absolute -top-3 left-6 px-2 py-1 text-[9px] font-black uppercase tracking-[0.28em]"
                  style={{ background: C.volt, color: C.ink }}
                >
                  {p.badge}
                </div>
              )}
              <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-2" style={{ color: C.dim }}>{p.sub}</div>
              <div className="text-xl md:text-2xl font-black uppercase tracking-tight mb-4">{p.title}</div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl md:text-5xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: C.volt }}>
                  {p.per}
                </span>
                <span className="text-xs uppercase tracking-widest font-bold" style={{ color: C.dim }}>{p.unit}</span>
              </div>
              <button
                onClick={() => openCheckout(p)}
                className="mt-auto inline-flex items-center justify-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em] border-2 transition-all hover:-translate-y-0.5"
                style={{
                  background: p.highlight ? C.volt : 'transparent',
                  color: p.highlight ? C.ink : '#fff',
                  borderColor: C.volt,
                }}
                data-testid={`pricing-tier-buy-button-${p.id}`}
              >
                Buy Now <ArrowRight className="w-4 h-4" />
              </button>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-center" style={{ color: C.dim }}>
                Secure Stripe checkout · one-time payment
              </div>
            </div>
          ))}
        </div>

        {/* Charter invoice CTA */}
        <div className="mt-10 md:mt-14 p-6 md:p-8 border-2 flex flex-col md:flex-row md:items-center gap-4 md:gap-8" style={{ background: C.ink, borderColor: C.subtle }}>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-2" style={{ color: C.mint }}>
              Charter Funded Family?
            </div>
            <div className="text-lg md:text-xl font-black uppercase tracking-tight mb-1">Taylion or Epic California — we invoice your charter directly.</div>
            <div className="text-sm" style={{ color: C.dim }}>Tap below and Margaret will initiate the vendor packet.</div>
          </div>
          <button
            onClick={() => setCharterOpen(true)}
            className="inline-flex items-center justify-center gap-2 py-3 px-6 text-xs font-black uppercase tracking-[0.22em]"
            style={{ background: C.mint, color: C.ink }}
            data-testid="charter-invoice-request-button"
          >
            <FileText className="w-4 h-4" /> Request Charter Invoice
          </button>
        </div>
      </div>

      <AnimatePresence>
        {checkoutTier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)' }}
            onClick={() => !payLoading && setCheckoutTier(null)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg p-6 md:p-8 border-2"
              style={{ background: C.card, borderColor: C.volt }}
              data-testid="prep-pod-checkout-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-1" style={{ color: C.volt }}>
                    Prep Pod · {checkoutTier.title}
                  </div>
                  <div className="text-xl font-black uppercase tracking-tight">
                    {checkoutTier.per} <span style={{ color: C.dim }} className="text-sm font-bold">· {checkoutTier.unit}</span>
                  </div>
                </div>
                <button onClick={() => !payLoading && setCheckoutTier(null)} className="p-1" data-testid="prep-pod-checkout-close"><X className="w-5 h-5" /></button>
              </div>
              <div className="mb-4 p-3 border-l-2" style={{ borderColor: C.volt, background: C.ink }}>
                <div className="text-[10px] uppercase tracking-widest font-black mb-1" style={{ color: C.dim }}>What happens next</div>
                <div className="text-xs" style={{ color: '#fff' }}>
                  Secure Stripe checkout opens. After payment, Margaret texts you the schedule within 24 hours.
                </div>
              </div>
              <div className="space-y-3">
                <Input label="Parent Name *" value={checkoutForm.parent_name} onChange={(v) => setPay('parent_name', v)} testId="prep-pod-checkout-parent-name" />
                <Input label="Email *" type="email" value={checkoutForm.parent_email} onChange={(v) => setPay('parent_email', v)} testId="prep-pod-checkout-parent-email" />
                <Input label="Phone" type="tel" value={checkoutForm.parent_phone} onChange={(v) => setPay('parent_phone', v)} testId="prep-pod-checkout-parent-phone" />
                <Input label="Child's Name" value={checkoutForm.child_name} onChange={(v) => setPay('child_name', v)} testId="prep-pod-checkout-child-name" />
                <Input label="Child's Grade (TK / K / 1st / 2nd)" value={checkoutForm.child_grade} onChange={(v) => setPay('child_grade', v)} testId="prep-pod-checkout-child-grade" />
                <input type="text" value={checkoutForm.website} onChange={(e) => setPay('website', e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />
              </div>
              <button
                onClick={startCheckout}
                disabled={payLoading}
                className="mt-6 w-full py-4 text-xs font-black uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: C.volt, color: C.ink }}
                data-testid="prep-pod-checkout-submit"
              >
                {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {payLoading ? 'Opening secure checkout...' : `Pay ${checkoutTier.per} with Stripe`}
              </button>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-center" style={{ color: C.dim }}>
                Powered by Stripe · one-time payment · no auto-renew
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {charterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)' }}
            onClick={() => setCharterOpen(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg p-6 md:p-8 border-2"
              style={{ background: C.card, borderColor: C.mint }}
              data-testid="charter-request-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-1" style={{ color: C.mint }}>Charter Invoice Request</div>
                  <div className="text-xl font-black uppercase tracking-tight">Let Margaret Handle It.</div>
                </div>
                <button onClick={() => setCharterOpen(false)} className="p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Charter</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { v: 'taylion', label: 'Taylion' },
                      { v: 'epic_california', label: 'Epic California' },
                    ].map((c) => (
                      <button
                        key={c.v}
                        onClick={() => setC('charter', c.v)}
                        className="py-3 border-2 text-xs font-black uppercase tracking-widest"
                        style={{
                          background: charterForm.charter === c.v ? C.mint : 'transparent',
                          color: charterForm.charter === c.v ? C.ink : '#fff',
                          borderColor: charterForm.charter === c.v ? C.mint : C.subtle,
                        }}
                        data-testid={`charter-choice-${c.v}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Input label="Parent Name" value={charterForm.parent_name} onChange={(v) => setC('parent_name', v)} testId="charter-parent-name" />
                <Input label="Email" type="email" value={charterForm.parent_email} onChange={(v) => setC('parent_email', v)} testId="charter-parent-email" />
                <Input label="Phone" type="tel" value={charterForm.parent_phone} onChange={(v) => setC('parent_phone', v)} testId="charter-parent-phone" />
                <Input label="Child's Name" value={charterForm.child_name} onChange={(v) => setC('child_name', v)} testId="charter-child-name" />
                <input type="text" value={charterForm.website} onChange={(e) => setC('website', e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />
              </div>
              <button
                onClick={submitCharter}
                disabled={submitting}
                className="mt-6 w-full py-4 text-xs font-black uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: C.mint, color: C.ink }}
                data-testid="charter-submit-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send To Margaret
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// Tip / Donation — free-form Stripe checkout for any amount ($5–$5000)
// ═══════════════════════════════════════════════════════════════════════════
const TIP_PRESETS = [10, 25, 50, 100];

const TipSection = () => {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);

  const chosenAmount = () => {
    const c = parseFloat(custom);
    if (!isNaN(c) && c > 0) return c;
    return amount;
  };

  const submit = async () => {
    const a = chosenAmount();
    if (isNaN(a) || a < 5) return toast.error('Minimum tip is $5');
    if (a > 5000) return toast.error('Maximum tip is $5,000');
    setBusy(true);
    try {
      const r = await api.post('/prep-pod/tip', {
        amount: Number(a.toFixed(2)),
        tipper_name: name || undefined,
        tipper_email: email || undefined,
        message: message || undefined,
        origin: window.location.origin,
        website,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.error('Could not open the tip checkout — try again');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tip session failed — try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="tip" className="py-14 md:py-24 scroll-mt-8" style={{ background: C.ink }} data-testid="prep-pod-tip">
      <div className="container mx-auto max-w-3xl px-6">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.peach }}>
          Support The Pod
        </div>
        <h2 className="uppercase leading-[0.95] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3.25rem)' }}>
          Send A <span style={{ color: C.peach }}>Tip.</span>
        </h2>
        <p className="text-sm md:text-base mb-8 max-w-xl" style={{ color: C.dim, lineHeight: 1.6 }}>
          Any dollar you give funds supplies, coaches, and scholarship seats for kids who need The Prep Pod but can't yet pay for it. There's no minimum obligation — pick your amount.
        </p>

        <div className="p-6 md:p-8 border-2" style={{ background: C.card, borderColor: C.subtle }}>
          <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-3" style={{ color: C.dim }}>Pick an amount</div>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {TIP_PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => { setAmount(v); setCustom(''); }}
                className="py-3 border-2 text-sm font-black uppercase tracking-widest transition-all"
                style={{
                  background: (!custom && amount === v) ? C.peach : 'transparent',
                  color: (!custom && amount === v) ? C.ink : '#fff',
                  borderColor: (!custom && amount === v) ? C.peach : C.subtle,
                }}
                data-testid={`tip-preset-${v}`}
              >
                ${v}
              </button>
            ))}
          </div>
          <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-1" style={{ color: C.dim }}>Or enter a custom amount</div>
            <div className="flex items-center gap-2 border-2 px-3 py-2" style={{ borderColor: C.subtle, background: C.ink }}>
              <span className="text-lg font-black" style={{ color: C.peach }}>$</span>
              <input
                type="number"
                min="5"
                max="5000"
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Custom amount (min $5)"
                className="flex-1 bg-transparent outline-none text-white text-lg font-bold"
                data-testid="tip-custom-amount"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <Input label="Your Name (optional)" value={name} onChange={setName} testId="tip-name" />
            <Input label="Your Email (optional, for receipt)" type="email" value={email} onChange={setEmail} testId="tip-email" />
          </div>
          <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-1" style={{ color: C.dim }}>Message (optional)</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 400))}
              rows={3}
              placeholder="A note for Margaret and the coaches..."
              className="w-full px-3 py-2 border-2 bg-transparent text-white text-sm"
              style={{ borderColor: C.subtle, background: C.ink }}
              data-testid="tip-message"
            />
          </div>
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />

          <button
            onClick={submit}
            disabled={busy}
            className="w-full py-4 text-xs font-black uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: C.peach, color: C.ink }}
            data-testid="tip-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            {busy ? 'Opening secure checkout...' : `Send $${chosenAmount().toFixed(chosenAmount() % 1 === 0 ? 0 : 2)} With Stripe`}
          </button>
          <div className="mt-3 text-[10px] uppercase tracking-widest text-center" style={{ color: C.dim }}>
            Powered by Stripe · one-time gift · no auto-renew
          </div>
        </div>
      </div>
    </section>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// Footer
// ═══════════════════════════════════════════════════════════════════════════
const Footer = () => (
  <footer className="py-14 border-t" style={{ background: C.ink, borderColor: C.subtle }} data-testid="prep-pod-footer">
    <div className="container mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-8">
      <div>
        <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-2" style={{ color: C.volt }}>The Prep Pod</div>
        <div className="text-2xl md:text-3xl font-black uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Handlelife HQ · Chatsworth, CA
        </div>
        <div className="text-sm mt-2" style={{ color: C.dim }}>20611 Prairie St, Chatsworth, CA</div>
      </div>
      <div className="space-y-2">
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: C.dim }}>Contact</div>
        <a href="mailto:Margaret@prsvr.com" className="flex items-center gap-2 text-sm font-bold hover:underline">
          <Mail className="w-4 h-4" style={{ color: C.volt }} /> Margaret@prsvr.com
        </a>
        <a href="https://scholarathlete.academy/pod" className="flex items-center gap-2 text-sm font-bold hover:underline" style={{ color: C.dim }}>
          scholarathlete.academy/pod
        </a>
      </div>
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: C.dim }}>© 2026</div>
        <div className="text-sm mt-1" style={{ color: C.dim }}>PRSVR Education &amp; Scholar Athlete Academy.<br/>All Rights Reserved.</div>
      </div>
    </div>
  </footer>
);

// ═══════════════════════════════════════════════════════════════════════════
// Confirmation modal (shared by RSVP / Application / Charter)
// ═══════════════════════════════════════════════════════════════════════════
const ConfirmationModal = ({ payload, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.85)' }}
    onClick={onClose}
    data-testid="prep-pod-confirmation-modal"
  >
    <motion.div
      initial={{ y: 30, scale: 0.98, opacity: 0 }}
      animate={{ y: 0, scale: 1, opacity: 1 }}
      exit={{ y: 30, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-lg p-8 md:p-12 border-2 relative"
      style={{ background: C.card, borderColor: C.volt }}
    >
      <button onClick={onClose} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
      <div
        className="w-14 h-14 mb-6 flex items-center justify-center"
        style={{ background: C.volt, color: C.ink }}
      >
        <Check className="w-8 h-8" />
      </div>
      <div className="text-[10px] font-black uppercase tracking-[0.32em] mb-2" style={{ color: C.volt }}>Locked In</div>
      <h2
        className="uppercase leading-[0.95] mb-3"
        style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
      >
        {payload.title}
      </h2>
      <p className="text-sm md:text-base leading-relaxed mb-6" style={{ color: C.dim }}>{payload.subtitle}</p>
      <div className="p-4 border-l-4" style={{ background: C.ink, borderColor: C.volt }}>
        <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: C.volt }}>Location</div>
        <div className="text-base font-bold mt-1">Handlelife HQ</div>
        <div className="text-sm" style={{ color: C.dim }}>20611 Prairie St, Chatsworth, CA</div>
        <a
          href="https://maps.google.com/?q=20611+Prairie+St,+Chatsworth,+CA"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest hover:underline"
          style={{ color: C.volt }}
        >
          Open in Maps <ArrowRight className="w-3 h-3" />
        </a>
      </div>
      <button
        onClick={onClose}
        className="mt-6 w-full py-3 text-xs font-black uppercase tracking-[0.22em]"
        style={{ background: '#fff', color: C.ink }}
      >
        All Set
      </button>
    </motion.div>
  </motion.div>
);

// ─── Little shared building blocks ─────────────────────────────────────────
const Label = ({ children }) => (
  <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: C.dim }}>{children}</div>
);

const Input = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-transparent border-2 px-4 py-3 text-sm font-medium outline-none focus:border-yellow-400"
      style={{ borderColor: C.subtle, color: '#fff' }}
      data-testid={testId}
    />
  </div>
);
